import {
  getBmi,
  getDaysTillGoalDate,
  getFasterProgressDaysDiff,
  getLocalizedDateLabel,
  getOfferId,
  getBasicOffers,
} from "../helpers"
import exitIntentPopup from "../exitIntentPopup"
import { PRICE_TYPES } from "../dataStore"
import { TrackingManager } from "../../../../../lib/common/js/services/tracking/trackingManager"

const ABANDONED_CART_PATH = "abandoned-basket-plan-selection"

export default (getView) =>
  ({ nextStepPath, exitIntentPath, activeStepPath, secondExitIntentPath }) => ({
    ...getView(nextStepPath),
    ...exitIntentPopup(exitIntentPath, secondExitIntentPath),

    paymentMethods: [],
    offers: [],
    activeOffer: null,
    basicOffers: null,
    basicOfferDiscount: null,
    showFetchError: false,
    paymentError: false,
    isSpinnerActive: false,
    internalTracker: new TrackingManager(
      window.trackingConfig.enabledTrackingServices,
      pipelineConfig,
    ),

    init() {
      window.addEventListener("pageshow", (event) => {
        history.scrollRestoration = "manual"
        window.scrollTo(0, 0)

        if (event.persisted) {
          const isTrialExitIntent = sessionStorage.getItem("trialExitIntent") === "true"
          this.isSpinnerActive = false

          if (this.store.isG2Variant() && isTrialExitIntent) {
            this.store.values.exitIntent = true
            this.store.values.trialExitIntent = true
            this.initOffers()
            sessionStorage.removeItem("trialExitIntent")
          }
        }
      })

      this.commonInit()

      if (this.shouldInitializeAbandonedCartState()) {
        this.initializeAbandonedCartState()
      }

      // Init Klaviyo user and track page, cause u can start tracking after init only. Not to miss any events ^^ (due to AC email)
      this.internalTracker.isUserIdentified({}, "klaviyo").then((isIdentified) => {
        if (!isIdentified) {
          this.internalTracker.trackIdentify({
            customData: { email: this.store.values.email },
            destinationService: "klaviyo",
            callBackFn: () => {
              this.internalTracker.trackPageView({}, "klaviyo")
            },
          })
        }
      })

      this.initOffers()

      this.initExitIntentPopup()
    },

    initBasicExitIntentOffer() {
      const basicExitIntentOffer = pipelineConfig.offers.filter(
        (offer) => offer.type && offer.type.toLowerCase() === "basic_exit_intent",
      )

      return basicExitIntentOffer[0]
    },

    initOffers() {
      // Restore purchased offer, cause active offer is overwritten during upsell
      this.store.restorePurchasedBasicOffer()

      const offers = getBasicOffers(pipelineConfig.offers)
      this.offers = offers

      let storedOffer = this.store.values.activeOffer
      const isStoredOfferBasic = storedOffer && storedOffer.type.toLowerCase() === "basic"
      const basicOffers = pipelineConfig.offers.filter((offer) => this.store.isBasicOffer(offer))

      // Active offer should always be basic for this view
      if (!storedOffer || !isStoredOfferBasic) {
        storedOffer = offers.find((offer) => offer.customData.default_select === "true")
      }

      if (this.store.values.trialExitIntent) {
        this.activeOffer = this.initBasicExitIntentOffer()
        this.store.setActiveOffer(this.activeOffer)
      } else {
        this.activeOffer = storedOffer || offers[0]
      }

      this.basicOffers = basicOffers

      this.store.values.activeOffer = this.activeOffer
    },

    // Helper functions
    isMetric() {
      return this.store.values.measurementSystem === "metric"
    },

    getDiscountKey(isTrial, exitIntent) {
      if (isTrial && exitIntent) {
        return "trial_exit_intent_price_discount"
      }
      if (isTrial) {
        return "trial_price_discount"
      }
      if (exitIntent) {
        return "exit_intent_discount"
      }
      return "discount"
    },

    getPeriodPriceKey(isTrial, exitIntent) {
      if (isTrial && exitIntent) {
        return "trial_exit_intent_price_per_period"
      }
      if (isTrial) {
        return "trial_price_per_period"
      }
      if (exitIntent) {
        return "period_price_exit_intent"
      }
      return "period_price"
    },

    // BMI calculations
    getBmi() {
      const isMetric = this.isMetric()
      return getBmi(this.store.values, isMetric).toFixed(2)
    },

    getTargetBmi() {
      const { heightCm, heightFt, heightIn, targetWeightKg, targetWeightLbs } = this.store.values

      const values = {
        heightCm,
        heightFt,
        heightIn,
        weightKg: targetWeightKg,
        weightLbs: targetWeightLbs,
      }

      const isMetric = this.isMetric()
      return getBmi(values, isMetric).toFixed(2)
    },

    daysToMonths(days) {
      const months = Math.floor(days / 30)
      const leftover = days % 30
      const result = months + (leftover >= 21 ? 1 : 0)

      return result === 0 ? 1 : result
    },

    getGoalDateLabel() {
      const isMetric = this.isMetric()
      const { weightKg, weightLbs, targetWeightKg, targetWeightLbs, goalPaceKg, goalPaceLbs } =
        this.store.values

      const daysTillGoalDate = getDaysTillGoalDate(
        {
          weightKg,
          weightLbs,
          targetWeightKg,
          targetWeightLbs,
          goalPaceKg,
          goalPaceLbs,
        },
        isMetric,
      )

      const fasterProgressDaysDiff = getFasterProgressDaysDiff(this.store.values, isMetric)
      const finalDaysTillGoalDate = daysTillGoalDate - fasterProgressDaysDiff

      const goalDate = new Date()
      goalDate.setDate(goalDate.getDate() + finalDaysTillGoalDate)

      return this.store.isG2Variant()
        ? this.daysToMonths(finalDaysTillGoalDate)
        : getLocalizedDateLabel(goalDate, true)
    },

    isOfferActive(offer) {
      const offerId = getOfferId(offer)
      const activeOfferId = this.activeOffer && getOfferId(this.activeOffer)
      return offerId === activeOfferId
    },

    isBestDeal(offer) {
      const exitIntent = this.store.values.exitIntent ?? false // Default to false if not set

      if (this.isOfferActive(offer)) {
        this.store.setActiveOffer(offer)
      }

      // Get the period price key and price for the current offer
      const periodPriceKey = this.getPeriodPriceKey(this.store.isOfferTrial(offer), exitIntent)
      const periodPrice = parseFloat(offer.customData[periodPriceKey])

      if (isNaN(periodPrice)) {
        return false
      }

      // Gather period prices for all offers based on the correct keys
      const allOffersPeriodPrices = this.offers
        .map((o) => {
          const otherPeriodPriceKey = this.getPeriodPriceKey(this.store.isOfferTrial(o), exitIntent)
          const otherPeriodPrice = parseFloat(o.customData[otherPeriodPriceKey])
          return isNaN(otherPeriodPrice) ? null : otherPeriodPrice
        })
        .filter((price) => price !== null)

      const minPrice = Math.min(...allOffersPeriodPrices)

      // Check if the current offer has the minimum price and is unique
      return (
        periodPrice === minPrice &&
        allOffersPeriodPrices.indexOf(periodPrice) ===
          allOffersPeriodPrices.lastIndexOf(periodPrice)
      )
    },

    getFormattedDiscountPercentage(activeOffer = this.activeOffer) {
      const { exitIntent } = this.store.values
      const isTrial = this.store.isOfferTrial(activeOffer)
      const discountKey = this.getDiscountKey(isTrial, exitIntent)

      return activeOffer.customData[discountKey]
    },

    getFormattedHighestDiscount() {
      return Math.max(
        ...this.basicOffers
          .map((offer) => {
            const isTrial = this.store.isOfferTrial(offer)
            const discountKey = this.getDiscountKey(isTrial, true)
            const oldDiscountKey = this.getDiscountKey(isTrial, false)
            this.basicOfferDiscount = offer.customData[oldDiscountKey]

            return parseFloat(offer.customData[discountKey])
          })
          .filter((v) => v !== undefined),
      )
    },

    getFormattedDiscount(activeOffer) {
      const { discount_amount = 0 } = activeOffer.customData

      if (!discount_amount) {
        return ""
      }

      return this.store.getFormattedPrice(discount_amount)
    },

    getPriceType(offer) {
      if (!this.store.isBasicOffer(offer)) return PRICE_TYPES.STANDARD

      const isTrialOffer = this.store.isOfferTrial(offer)
      const { exitIntent } = this.store.values

      if (isTrialOffer && exitIntent) return PRICE_TYPES.EXIT_INTENT_TRIAL
      if (isTrialOffer) return PRICE_TYPES.TRIAL
      if (exitIntent) return PRICE_TYPES.EXIT_INTENT

      return PRICE_TYPES.STANDARD
    },

    getPricePerPeriod(offer) {
      const priceType = this.getPriceType(offer)
      const periodPriceKey = this.getPeriodPriceKey(
        this.store.isOfferTrial(offer),
        this.store.values.exitIntent,
      )

      return this.store.getFormattedPrice(
        offer.customData[periodPriceKey] || offer.customData[priceType],
      )
    },

    // Abandoned cart state initialization
    shouldInitializeAbandonedCartState() {
      return (
        activeStepPath.includes(ABANDONED_CART_PATH) && !this.store.values.isAbandonedCartViewed
      )
    },

    async initializeAbandonedCartState() {
      Object.assign(this.store.values, {
        isAbandonedCartViewed: true,
        exitIntent: false,
        showDiscountModal: false,
      })

      await this.store.push()
    },

    async backButtonHandler() {
      const { values } = this.store

      this.internalTracker.setEventToSessionStorage({ clickName: "Back Clicked" })

      if (this.store.shouldShowExitIntentPopup()) {
        const token = values.token
        const tokenPath = token ? `${exitIntentPath}#stored-token=${token}` : popupStepPath

        window.location.replace(tokenPath)
      } else {
        history.back()
      }
    },

    // UI interactions
    scrollToElement(id, trackingEventName) {
      const element = document.getElementById(id)

      this.internalTracker.trackClick({
        clickName: trackingEventName,
      })

      if (element) {
        element.scrollIntoView({
          behavior: "smooth",
        })
      }
    },

    selectOffer(offer) {
      this.internalTracker.trackClick({ clickName: "Plan Selected" })
      this.activeOffer = offer
    },

    // Tracking and updates
    async completePayment() {
      this.store.values.completedAt = new Date()
      await this.store.push()
    },

    async trackVisitCheckout() {
      this.store.values.status = "VISIT_CHECKOUT"
      await this.store.push()
    },

    async updateOrderUtms() {
      await this.store.push()
    },

    isNutritionistBubbleNeeded(translationKey) {
      const bubbleNeededKeys = [
        "paymentPremium",
        "paymentChat",
        "paymentShoppinglist",
        "paymentBundle",
      ]
      return bubbleNeededKeys.includes(translationKey)
    },

    async submitOffer() {
      this.isSpinnerActive = true

      try {
        const trackingEventLabel = this.store.values.trialExitIntent
          ? "Start Trial Clicked"
          : "Get Plan Clicked"

        this.internalTracker.setEventToSessionStorage({ clickName: trackingEventLabel })
        await this.store.setActiveOffer(this.activeOffer)

        window.location = nextStepPath
      } catch (error) {
        console.error("Error submitting offer:", error)
      } finally {
        this.isSpinnerActive = false
      }
    },
  })
